import styled from "styled-components";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { selectUser } from "../../features/userSlice";
import {
    CATEGORIES,
    TAG_SUGGESTIONS,
    slugify,
    isCommunitySlugAvailable,
    createCommunity,
} from "../../utils/communities";
import { uploadToCloudinary } from "../../utils/cloudinary";

export const CreateCommunityModal = ({ open, onClose }) => {
    const user = useSelector(selectUser);
    const history = useHistory();

    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [slugState, setSlugState] = useState("idle"); // idle | checking | available | taken
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("General");
    const [visibility, setVisibility] = useState("public");
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [avatar, setAvatar] = useState("");
    const [banner, setBanner] = useState("");
    const [uploading, setUploading] = useState("");
    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);

    if (!open) return null;

    const handleNameChange = async (value) => {
        setName(value);
        const s = slugify(value);
        setSlug(s);
        if (!s) {
            setSlugState("idle");
            return;
        }
        setSlugState("checking");
        const avail = await isCommunitySlugAvailable(s);
        setSlugState(avail ? "available" : "taken");
    };

    const handleTagKey = (e) => {
        if (e.key === "Enter" && tagInput.trim()) {
            e.preventDefault();
            const t = tagInput.trim();
            if (!tags.includes(t) && tags.length < 10) setTags([...tags, t]);
            setTagInput("");
        }
    };

    const addSuggestedTag = (t) => {
        if (!tags.includes(t) && tags.length < 10) setTags([...tags, t]);
    };

    const handleUpload = async (file, type) => {
        if (!file) return;
        setUploading(type);
        try {
            const res = await uploadToCloudinary(file, type === "avatar" ? "avatar" : "post");
            if (type === "avatar") setAvatar(res.url);
            else setBanner(res.url);
        } catch (e) {
            setError("Image upload failed");
        } finally {
            setUploading("");
        }
    };

    const handleCreate = async () => {
        setError("");
        if (name.trim().length < 3 || name.trim().length > 50) {
            setError("Name must be 3-50 characters.");
            return;
        }
        if (!slug) {
            setError("Name must be letters or numbers.");
            return;
        }
        if (slugState !== "available") {
            setError(slugState === "taken" ? "That community URL is taken." : "Checking availability...");
            return;
        }
        setCreating(true);
        try {
            await createCommunity(
                {
                    name: name.trim(),
                    slug,
                    description: description.trim().slice(0, 500),
                    category,
                    visibility,
                    tags,
                    avatar,
                    banner,
                },
                user
            );
            onClose();
            history.push(`/c/${slug}`);
        } catch (e) {
            setError("Could not create community. Try again.");
        } finally {
            setCreating(false);
        }
    };

    return (
        <Backdrop onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <Card>
                <div className="card-head">
                    <h2>Create Community</h2>
                    <button className="close" onClick={onClose}>×</button>
                </div>

                {/* Banner + Avatar uploads */}
                <div className="media">
                    <div className="banner-upload" onClick={() => document.getElementById("cc-banner").click()}>
                        {banner ? <img src={banner} alt="banner" /> : <span>{uploading === "banner" ? "Uploading..." : "Add a banner (optional)"}</span>}
                        <input id="cc-banner" type="file" accept="image/*" hidden onChange={(e) => handleUpload(e.target.files[0], "banner")} />
                    </div>
                    <div className="avatar-upload" onClick={() => document.getElementById("cc-avatar").click()}>
                        {avatar ? <img src={avatar} alt="avatar" /> : <span>{uploading === "avatar" ? "..." : "+"}</span>}
                        <input id="cc-avatar" type="file" accept="image/*" hidden onChange={(e) => handleUpload(e.target.files[0], "avatar")} />
                    </div>
                </div>

                <label className="field">
                    <span>Community name</span>
                    <input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g. Computer Science" maxLength={50} />
                </label>

                <div className="field">
                    <span>Community URL</span>
                    <div className="slug-row">
                        <span className="slug-prefix">fellora.com/c/</span>
                        <input value={slug} readOnly placeholder="auto-generated" />
                        <Status state={slugState}>
                            {slugState === "available" ? "✓ Available" : slugState === "taken" ? "✗ Taken" : slugState === "checking" ? "Checking..." : ""}
                        </Status>
                    </div>
                </div>

                <label className="field">
                    <span>Description <em>(max 500)</em></span>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} rows={3} placeholder="What is this community about?" />
                </label>

                <div className="field row-2">
                    <label>
                        <span>Category</span>
                        <select value={category} onChange={(e) => setCategory(e.target.value)}>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Visibility</span>
                        <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                            <option value="public">Public</option>
                            <option value="restricted">Restricted</option>
                            <option value="private">Private</option>
                        </select>
                    </label>
                </div>

                <div className="field">
                    <span>Tags</span>
                    <input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKey}
                        placeholder="Type a tag and press Enter"
                    />
                    <div className="tags">
                        {tags.map((t) => (
                            <button key={t} className="tag" onClick={() => setTags(tags.filter((x) => x !== t))}>{t} ×</button>
                        ))}
                        <div className="suggested">
                            {TAG_SUGGESTIONS.filter((s) => !tags.includes(s)).slice(0, 8).map((s) => (
                                <button key={s} className="suggest" onClick={() => addSuggestedTag(s)}>+ {s}</button>
                            ))}
                        </div>
                    </div>
                </div>

                {error && <div className="error">{error}</div>}

                <div className="card-foot">
                    <button className="cancel" onClick={onClose}>Cancel</button>
                    <button className="create" onClick={handleCreate} disabled={creating}>
                        {creating ? "Creating..." : "Create Community"}
                    </button>
                </div>
            </Card>
        </Backdrop>
    );
};

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(10, 20, 40, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    backdrop-filter: blur(2px);
`;

const Card = styled.div`
    width: 100%;
    max-width: 560px;
    max-height: 92vh;
    overflow-y: auto;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(10, 20, 40, 0.25);
    padding: 1.2rem 1.4rem 1.4rem;

    .card-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;

        h2 { font-size: 20px; color: #0a1428; margin: 0; }

        .close {
            background: #f0ebe3;
            border: none;
            width: 32px; height: 32px;
            border-radius: 50%;
            font-size: 20px;
            cursor: pointer;
            color: #555;
            &:hover { background: #e2d9c9; }
        }
    }

    .media {
        position: relative;
        margin-bottom: 1rem;

        .banner-upload {
            height: 130px;
            border-radius: 12px;
            background: linear-gradient(135deg, #1DA1F2, #0d8ecf);
            cursor: pointer;
            overflow: hidden;
            display: flex; align-items: center; justify-content: center;
            color: white; font-size: 13px; font-weight: 600;

            img { width: 100%; height: 100%; object-fit: cover; }
        }

        .avatar-upload {
            position: absolute;
            left: 14px; bottom: -24px;
            width: 74px; height: 74px;
            border-radius: 50%;
            border: 4px solid white;
            background: linear-gradient(135deg, #c9a063, #e8c47d);
            cursor: pointer;
            overflow: hidden;
            display: flex; align-items: center; justify-content: center;
            color: white; font-size: 30px; font-weight: 700;

            img { width: 100%; height: 100%; object-fit: cover; }
        }
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 1rem;

        > span { font-size: 13px; font-weight: 600; color: #555; em { font-weight: 400; color: #999; } }

        input, textarea, select {
            border: 1px solid #e0d8cc;
            border-radius: 8px;
            padding: 9px 12px;
            font-size: 14px;
            font-family: inherit;
            &:focus { outline: none; border-color: #1DA1F2; }
        }

        textarea { resize: vertical; }
    }

    .row-2 {
        flex-direction: row;
        gap: 12px;
        > label { flex: 1; display: flex; flex-direction: column; gap: 6px; > span { font-size: 13px; font-weight: 600; color: #555; } }
    }

    .slug-row {
        display: flex;
        align-items: center;
        gap: 6px;

        .slug-prefix { color: #999; font-size: 13px; flex-shrink: 0; }
        input { flex: 1; background: #faf7f2; }
    }

    .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;

        .tag {
            border: 1px solid #1DA1F2;
            color: #1DA1F2;
            background: #e8f5fe;
            border-radius: 14px;
            padding: 3px 10px;
            font-size: 12px;
            cursor: pointer;
        }

        .suggested {
            display: flex; flex-wrap: wrap; gap: 6px; width: 100%; margin-top: 4px;

            .suggest {
                border: 1px dashed #c9a063;
                color: #6b4e2a;
                background: #fff8ec;
                border-radius: 14px;
                padding: 3px 10px;
                font-size: 12px;
                cursor: pointer;
            }
        }
    }

    .error {
        color: #d32f2f;
        font-size: 13px;
        background: #ffebee;
        padding: 8px 10px;
        border-radius: 8px;
        margin-bottom: 1rem;
    }

    .card-foot {
        display: flex;
        justify-content: flex-end;
        gap: 10px;

        button {
            padding: 9px 18px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
        }

        .cancel {
            background: #f0ebe3;
            color: #555;
            border: none;
        }

        .create {
            background: linear-gradient(135deg, #1DA1F2, #0d8ecf);
            color: white;
            border: none;
            &:disabled { opacity: 0.6; cursor: not-allowed; }
        }
    }
`;

const Status = styled.span`
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: ${(p) =>
        p.state === "available" ? "#22c55e" : p.state === "taken" ? "#e53935" : "#999"};
`;